import React from 'react';
import { Shield, Eye, Lock, Share2, FileText, ChevronRight, AlertCircle } from 'lucide-react';

interface SecurityAccessHomeProps {
  onNavigate: (section: string) => void;
}

export const SecurityAccessHome: React.FC<SecurityAccessHomeProps> = ({ onNavigate }) => {
  const securityFeatures = [
    {
      id: 'field-level-security',
      name: 'Field-Level Security',
      description: 'Control which roles can view and edit specific fields across all objects.',
      icon: Eye,
      iconColor: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
      available: true,
      stats: '247 fields configured'
    },
    {
      id: 'object-permissions',
      name: 'Object Permissions',
      description: 'Manage create, read, edit, and delete permissions for custom objects by role.',
      icon: Lock,
      iconColor: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      available: false,
      stats: null
    },
    {
      id: 'sharing-rules',
      name: 'Sharing Rules',
      description: 'Define record-level access rules to share data between roles and groups.',
      icon: Share2,
      iconColor: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/20',
      available: false,
      stats: null
    },
    {
      id: 'security-audit',
      name: 'Security Audit Logs',
      description: 'Track all security-related events, permission changes, and access attempts.',
      icon: FileText,
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
      available: false,
      stats: null
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
            <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Security & Access
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage user access, permissions, and security policies
            </p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
              Security & Access Controls
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Configure field-level permissions, object access, sharing rules, and track security events.
              Start with <strong>Field-Level Security</strong> to control who can view and edit specific fields.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">247</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Fields Secured</div>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <Eye className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">6</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Active Roles</div>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">84</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Restricted Fields</div>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
              <Shield className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">12</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Security Issues</div>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Security Features */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Security Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {securityFeatures.map((feature) => (
            <SecurityFeatureCard
              key={feature.id}
              feature={feature}
              onClick={() => {
                if (feature.available) {
                  onNavigate(feature.id);
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* Related Settings */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Related Settings
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => onNavigate('roles')}
            className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Roles & Permissions
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Manage user roles and their permissions
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>

          <button
            onClick={() => onNavigate('users')}
            className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  User Management
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Manage users and assign roles
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Security Feature Card Component
interface SecurityFeatureCardProps {
  feature: {
    id: string;
    name: string;
    description: string;
    icon: React.ElementType;
    iconColor: string;
    bgColor: string;
    available: boolean;
    stats: string | null;
  };
  onClick: () => void;
}

const SecurityFeatureCard: React.FC<SecurityFeatureCardProps> = ({ feature, onClick }) => {
  const Icon = feature.icon;

  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-all ${
        feature.available
          ? 'hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-700 cursor-pointer'
          : 'opacity-60 cursor-not-allowed'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 ${feature.bgColor} rounded-lg`}>
          <Icon className={`w-6 h-6 ${feature.iconColor}`} />
        </div>
        {feature.available ? (
          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded font-medium">
            Available
          </span>
        ) : (
          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded font-medium">
            Coming Soon
          </span>
        )}
      </div>

      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {feature.name}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        {feature.description}
      </p>

      {feature.stats && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {feature.stats}
          </span>
          {feature.available && (
            <ChevronRight className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          )}
        </div>
      )}
    </div>
  );
};
